import React, { useState, useCallback, useEffect } from 'react';
import ReactFlow, {
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mic, 
  Video, 
  FileText, 
  Mail, 
  Loader2,
  Zap,
  MessageCircle,
  Calendar,
  Search,
  LogOut,
  User,
  HelpCircle,
  Clapperboard,
  Newspaper
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import AudienceNode from '../components/nodes/AudienceNode';
import ContentTypeNode from '../components/nodes/ContentTypeNode';
import TopicNode from '../components/nodes/TopicNode';
import GenerationModal from '../components/GenerationModal';
import KeywordsPanel from '../components/KeywordsPanel';
import ChatPanel from '../components/ChatPanel';
import PatientNavigator from '../components/PatientNavigator';
import ContentHistory from '../components/ContentHistory';
import ContentCalendar from '../components/ContentCalendar';
import HelpGuide from '../components/HelpGuide';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Content type icons and labels
const CONTENT_TYPES = {
  podcast: { icon: Mic, label: 'Podcast', output: 'Script' },
  youtube_short: { icon: Video, label: 'YouTube Short', output: 'Script' },
  meta_reel: { icon: Video, label: 'Meta Reel', output: 'Script' },
  blog: { icon: FileText, label: 'Blog', output: 'Draft' },
  newsletter: { icon: Mail, label: 'Newsletter', output: 'Draft' },
};

// Node types registration
const nodeTypes = {
  audience: AudienceNode,
  contentType: ContentTypeNode,
  topic: TopicNode,
};

// Initial nodes - just the center hub and audience nodes (no images, solid colors)
const initialNodes = [
  {
    id: 'hub',
    type: 'default',
    position: { x: 400, y: 300 },
    data: { label: '' },
    style: {
      width: 80,
      height: 80,
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #FF7F00 0%, #0070FF 100%)',
      border: '3px solid #0070FF',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 8px 30px rgba(0, 112, 255, 0.3)',
    },
    draggable: false,
  },
  {
    id: 'patients',
    type: 'audience',
    position: { x: 100, y: 200 },
    data: { 
      label: 'Patients', 
      description: 'Health & Wellness',
      active: false,
    },
  },
  {
    id: 'sponsors',
    type: 'audience',
    position: { x: 600, y: 100 },
    data: { 
      label: 'Sponsors', 
      description: 'Study Opportunities',
      active: false,
    },
  },
  {
    id: 'physicians',
    type: 'audience',
    position: { x: 600, y: 400 },
    data: { 
      label: 'Physicians', 
      description: 'Research Outreach',
      active: false,
    },
  },
];

const initialEdges = [
  { id: 'hub-patients', source: 'hub', target: 'patients', animated: true, style: { stroke: '#E4E4E7', strokeWidth: 2 } },
  { id: 'hub-sponsors', source: 'hub', target: 'sponsors', animated: true, style: { stroke: '#E4E4E7', strokeWidth: 2 } },
  { id: 'hub-physicians', source: 'hub', target: 'physicians', animated: true, style: { stroke: '#E4E4E7', strokeWidth: 2 } },
];

const ContentMindmap = ({ onNavigateToSEO, onNavigateToStudio, onNavigateToNews, user, onLogout }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedAudience, setSelectedAudience] = useState(null);
  const [selectedContentType, setSelectedContentType] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [topics, setTopics] = useState([]);
  const [viralHooks, setViralHooks] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [savedKeywords, setSavedKeywords] = useState([]);
  const [isLoadingTopics, setIsLoadingTopics] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showKeywordsPanel, setShowKeywordsPanel] = useState(false);
  const [showChatPanel, setShowChatPanel] = useState(false);
  const [showPatientNavigator, setShowPatientNavigator] = useState(false);
  const [showContentHistory, setShowContentHistory] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showHelpGuide, setShowHelpGuide] = useState(false);
  const [contentHistory, setContentHistory] = useState([]);
  const [patientContext, setPatientContext] = useState({
    therapeuticArea: null,
    disease: null,
    contentCategory: null
  });

  // Fetch saved keywords and content history on mount
  useEffect(() => {
    fetchSavedKeywords();
    fetchContentHistory();
  }, []);

  const fetchSavedKeywords = async () => {
    try {
      const response = await axios.get(`${API}/keywords`);
      setSavedKeywords(response.data);
    } catch (error) {
      console.error('Error fetching keywords:', error);
    }
  };

  const fetchContentHistory = async () => {
    try {
      const response = await axios.get(`${API}/content/history`);
      setContentHistory(response.data);
    } catch (error) {
      console.error('Error fetching content history:', error);
    }
  };

  // Handle topic click - show generation modal (declared first for dependency)
  const handleTopicClick = useCallback((topicData) => {
    setSelectedTopic({
      ...topicData,
      keywords: keywords
    });
    setShowModal(true);
  }, [keywords]);

  // Handle audience node click
  const handleAudienceClick = useCallback((audienceId) => {
    // Reset if same audience is clicked
    if (selectedAudience === audienceId) {
      setSelectedAudience(null);
      setSelectedContentType(null);
      setTopics([]);
      setViralHooks([]);
      setKeywords([]);
      setShowKeywordsPanel(false);
      setShowPatientNavigator(false);
      setPatientContext({ therapeuticArea: null, disease: null, contentCategory: null });
      setNodes(initialNodes);
      setEdges(initialEdges);
      return;
    }

    setSelectedAudience(audienceId);
    setSelectedContentType(null);
    setTopics([]);
    setViralHooks([]);
    setKeywords([]);
    setPatientContext({ therapeuticArea: null, disease: null, contentCategory: null });

    // Show patient navigator for patients
    if (audienceId === 'patients') {
      setShowPatientNavigator(true);
    } else {
      setShowPatientNavigator(false);
    }

    // Update nodes to show active state and add content type nodes
    const audienceNode = initialNodes.find(n => n.id === audienceId);
    const contentTypeNodes = Object.entries(CONTENT_TYPES).map(([key, value], index) => {
      const angle = (index * (Math.PI / 3)) - (Math.PI / 3);
      const radius = 200;
      const x = audienceNode.position.x + 70 + Math.cos(angle) * radius;
      const y = audienceNode.position.y + 70 + Math.sin(angle) * radius;
      
      return {
        id: `${audienceId}-${key}`,
        type: 'contentType',
        position: { x, y },
        data: { 
          contentType: key,
          label: value.label, 
          icon: value.icon,
          output: value.output,
          active: false,
        },
      };
    });

    const contentTypeEdges = Object.keys(CONTENT_TYPES).map((key) => ({
      id: `edge-${audienceId}-${key}`,
      source: audienceId,
      target: `${audienceId}-${key}`,
      animated: true,
      style: { stroke: '#0070FF', strokeWidth: 2 },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#0070FF' },
    }));

    setNodes([
      ...initialNodes.map(n => ({
        ...n,
        data: { ...n.data, active: n.id === audienceId }
      })),
      ...contentTypeNodes,
    ]);

    setEdges([
      ...initialEdges.map(e => ({
        ...e,
        style: { 
          ...e.style, 
          stroke: e.target === audienceId ? '#0070FF' : '#E4E4E7',
          strokeWidth: 2,
        }
      })),
      ...contentTypeEdges,
    ]);
  }, [selectedAudience, setNodes, setEdges]);

  // Handle content type click
  const handleContentTypeClick = useCallback(async (contentTypeId, contentType) => {
    if (!selectedAudience) return;

    // Reset if same content type is clicked
    if (selectedContentType === contentType) {
      handleAudienceClick(selectedAudience);
      setSelectedContentType(null);
      return;
    }

    setSelectedContentType(contentType);
    setIsLoadingTopics(true);
    setTopics([]);
    setViralHooks([]);
    setKeywords([]);
    setShowKeywordsPanel(true);

    // Update node active state
    setNodes(prevNodes => prevNodes.map(n => ({
      ...n,
      data: {
        ...n.data,
        active: n.type === 'contentType' ? n.id === contentTypeId : n.data.active,
      }
    })));

    try {
      const requestData = {
        audience: selectedAudience,
        content_type: contentType,
      };
      
      // Add patient context if available
      if (selectedAudience === 'patients' && patientContext.therapeuticArea) {
        requestData.therapeutic_area = patientContext.therapeuticArea;
        if (patientContext.disease) {
          requestData.disease = patientContext.disease;
        }
        if (patientContext.contentCategory) {
          requestData.content_category = patientContext.contentCategory;
        }
      }

      const response = await axios.post(`${API}/topics/generate`, requestData);

      const { topics: newTopics, viral_hooks: newHooks, keywords: newKeywords } = response.data;
      setTopics(newTopics);
      setViralHooks(newHooks);
      setKeywords(newKeywords || []);

      // Add topic nodes
      const contentNode = nodes.find(n => n.id === contentTypeId);
      if (contentNode) {
        const topicNodes = newTopics.map((topic, index) => {
          const angle = ((index - 2) * (Math.PI / 6));
          const radius = 220;
          const x = contentNode.position.x + Math.cos(angle) * radius;
          const y = contentNode.position.y + Math.sin(angle) * radius + 50;

          return {
            id: `topic-${index}`,
            type: 'topic',
            position: { x, y },
            data: {
              topic,
              viralHook: newHooks[index] || '',
              contentType,
              audience: selectedAudience,
              output: CONTENT_TYPES[contentType].output,
              onClick: handleTopicClick,
            },
          };
        });

        const topicEdges = newTopics.map((_, index) => ({
          id: `edge-topic-${index}`,
          source: contentTypeId,
          target: `topic-${index}`,
          animated: true,
          style: { stroke: '#FF7F00', strokeWidth: 2 },
        }));

        setNodes(prevNodes => [...prevNodes, ...topicNodes]);
        setEdges(prevEdges => [...prevEdges, ...topicEdges]);
      }

      toast.success('Topics generated!', {
        description: `${newTopics.length} viral topic ideas ready`,
      });
    } catch (error) {
      console.error('Error generating topics:', error);
      toast.error('Failed to generate topics', {
        description: error.response?.data?.detail || 'Please try again',
      });
    } finally {
      setIsLoadingTopics(false);
    }
  }, [selectedAudience, selectedContentType, nodes, setNodes, setEdges, handleAudienceClick, handleTopicClick, patientContext]);

  // Generate content
  const handleGenerateContent = useCallback(async () => {
    if (!selectedTopic) return;

    setIsGenerating(true);
    setGeneratedContent(null);

    try {
      const response = await axios.post(`${API}/content/generate`, {
        audience: selectedTopic.audience,
        content_type: selectedTopic.contentType,
        topic: selectedTopic.topic,
        viral_hook: selectedTopic.viralHook,
        keywords: selectedTopic.keywords,
      });

      setGeneratedContent(response.data);
      triggerConfetti();
      
      // Save to content history
      try {
        await axios.post(`${API}/content/save`, {
          ...response.data,
          content_type: selectedTopic.contentType,
          audience: selectedTopic.audience,
          topic: selectedTopic.topic,
          format_type: response.data.content_type,
          therapeutic_area: patientContext.therapeuticArea,
          disease: patientContext.disease,
          content_category: patientContext.contentCategory,
        });
        fetchContentHistory();
      } catch (saveError) {
        console.error('Error saving to history:', saveError);
      }
      
      toast.success('Content generated!', {
        description: `Your viral ${response.data.content_type} is ready`,
      });
    } catch (error) {
      console.error('Error generating content:', error);
      toast.error('Failed to generate content', {
        description: error.response?.data?.detail || 'Please try again',
      });
    } finally {
      setIsGenerating(false);
    }
  }, [selectedTopic, patientContext]);

  // Delete content from history
  const handleDeleteContent = async (contentId) => {
    try {
      await axios.delete(`${API}/content/history/${contentId}`);
      toast.success('Content deleted');
      fetchContentHistory();
    } catch (error) {
      toast.error('Failed to delete content');
    }
  };

  // Save keyword
  const handleSaveKeyword = async (keyword) => {
    try {
      await axios.post(`${API}/keywords`, {
        keyword,
        audience: selectedAudience || 'general',
        content_type: selectedContentType,
        therapeutic_area: patientContext.therapeuticArea,
        disease: patientContext.disease,
      });
      toast.success('Keyword saved!');
      fetchSavedKeywords();
    } catch (error) {
      toast.error('Failed to save keyword');
    }
  };

  // Delete keyword
  const handleDeleteKeyword = async (keywordId) => {
    try {
      await axios.delete(`${API}/keywords/${keywordId}`);
      toast.success('Keyword removed');
      fetchSavedKeywords();
    } catch (error) {
      toast.error('Failed to delete keyword');
    }
  };

  // Handle patient context selection
  const handlePatientContextChange = (context) => {
    setPatientContext(context);
  };

  // Confetti effect
  const triggerConfetti = () => {
    const colors = ['#FF7F00', '#0070FF', '#FFFFFF', '#1A1A2E'];
    const confettiCount = 50;

    for (let i = 0; i < confettiCount; i++) {
      const confetti = document.createElement('div');
      confetti.className = 'confetti';
      confetti.style.left = `${Math.random() * 100}vw`;
      confetti.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
      confetti.style.animationDelay = `${Math.random() * 0.5}s`;
      confetti.style.transform = `rotate(${Math.random() * 360}deg)`;
      document.body.appendChild(confetti);

      setTimeout(() => confetti.remove(), 3000);
    }
  };

  // Node click handler
  const onNodeClick = useCallback((event, node) => {
    if (node.type === 'audience') {
      handleAudienceClick(node.id);
    } else if (node.type === 'contentType') {
      handleContentTypeClick(node.id, node.data.contentType);
    } else if (node.type === 'topic') {
      handleTopicClick(node.data);
    }
  }, [handleAudienceClick, handleContentTypeClick, handleTopicClick]);

  // Get chat context
  const getChatContext = () => ({
    audience: selectedAudience,
    content_type: selectedContentType,
    therapeutic_area: patientContext.therapeuticArea,
    disease: patientContext.disease,
  });

  return (
    <div className="neural-studio" data-testid="content-mindmap">
      {/* Floating Header */}
      <motion.div 
        className="floating-header"
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 100 }}
      >
        <div className="flex items-center gap-3">
          <img src="/stryde-bird.jpg" alt="Stryde" className="w-6 h-6 object-contain" />
          <h1 className="font-heading text-lg font-bold tracking-tight" style={{color: '#0070FF'}}>STRYDE VIBE</h1>
        </div>
        {/* User Info & Logout */}
        <div className="flex items-center gap-3">
          {/* Help Button */}
          <button
            onClick={() => setShowHelpGuide(true)}
            className="p-2 hover:bg-[#0070FF]/10 rounded-full text-[#0070FF] hover:text-[#FF7F00] transition-colors"
            title="Help & Instructions"
            data-testid="help-btn"
          >
            <HelpCircle className="w-5 h-5" />
          </button>
          {user && (
            <>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full">
                <User className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-700 font-medium">{user.name || user.email}</span>
              </div>
              <button
                onClick={onLogout}
                className="p-2 hover:bg-red-50 rounded-full text-gray-400 hover:text-red-500 transition-colors"
                title="Sign out"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </motion.div>

      {/* SEO Button - Top Left */}
      <motion.button
        className="fixed top-24 left-6 z-50 px-4 py-2 rounded-full bg-[#0070FF] text-white flex items-center gap-2 shadow-lg hover:bg-[#0055CC] transition-colors text-sm font-medium"
        onClick={onNavigateToSEO}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        initial={{ x: -100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
        data-testid="seo-nav-btn"
      >
        <Search className="w-4 h-4" />
        SEO Tools
      </motion.button>

      {/* Video Studio Button - below SEO */}
      <motion.button
        className="fixed top-36 left-6 z-50 px-4 py-2 rounded-full bg-[#FF7F00] text-black flex items-center gap-2 shadow-lg hover:bg-[#FF9933] transition-colors text-sm font-semibold"
        onClick={onNavigateToStudio}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        initial={{ x: -100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ delay: 0.4 }}
        data-testid="studio-nav-btn"
      >
        <Clapperboard className="w-4 h-4" />
        Video Studio
      </motion.button>

      {/* News Studio Button - below Video Studio */}
      <motion.button
        className="fixed top-48 left-6 z-50 px-4 py-2 rounded-full bg-emerald-600 text-white flex items-center gap-2 shadow-lg hover:bg-emerald-700 transition-colors text-sm font-semibold"
        onClick={onNavigateToNews}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        initial={{ x: -100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
        data-testid="news-nav-btn"
      >
        <Newspaper className="w-4 h-4" />
        News Studio
      </motion.button>

      {/* Patient Navigator Panel */}
      <AnimatePresence>
        {showPatientNavigator && selectedAudience === 'patients' && (
          <PatientNavigator
            onContextChange={handlePatientContextChange}
            currentContext={patientContext}
          />
        )}
      </AnimatePresence>

      {/* Keywords Panel */}
      <AnimatePresence>
        {showKeywordsPanel && keywords.length > 0 && (
          <KeywordsPanel
            keywords={keywords}
            savedKeywords={savedKeywords}
            onSaveKeyword={handleSaveKeyword}
            onDeleteKeyword={handleDeleteKeyword}
            audience={selectedAudience}
            onClose={() => setShowKeywordsPanel(false)}
          />
        )}
      </AnimatePresence>

      {/* Chat Toggle Button */}
      <motion.button
        className="fixed bottom-24 right-6 z-50 w-14 h-14 rounded-full bg-[#0070FF] text-white flex items-center justify-center shadow-lg hover:bg-[#FF7F00] transition-colors"
        onClick={() => setShowChatPanel(!showChatPanel)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        data-testid="chat-toggle-btn"
      >
        <MessageCircle className="w-6 h-6" />
      </motion.button>

      {/* Top Right Buttons */}
      <div className="fixed top-24 right-6 z-50 flex gap-3">
        {/* Calendar Button */}
        <motion.button
          className="px-4 py-2 rounded-full bg-gradient-to-r from-[#0070FF] to-[#FF7F00] text-white flex items-center gap-2 shadow-lg hover:shadow-xl transition-all text-sm font-medium"
          onClick={() => setShowCalendar(true)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          data-testid="calendar-toggle-btn"
        >
          <Calendar className="w-4 h-4" />
          Calendar
        </motion.button>
        
        {/* Content History Button */}
        <motion.button
          className="px-4 py-2 rounded-full bg-[#0070FF] text-white flex items-center gap-2 shadow-lg hover:bg-[#FF7F00] transition-colors text-sm font-medium"
          onClick={() => setShowContentHistory(!showContentHistory)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          data-testid="history-toggle-btn"
        >
          <FileText className="w-4 h-4" />
          My Content ({contentHistory.length})
        </motion.button>
      </div>

      {/* Content History Panel */}
      <AnimatePresence>
        {showContentHistory && (
          <ContentHistory
            contents={contentHistory}
            onClose={() => setShowContentHistory(false)}
            onDelete={handleDeleteContent}
          />
        )}
      </AnimatePresence>

      {/* Content Calendar */}
      <AnimatePresence>
        {showCalendar && (
          <ContentCalendar
            isOpen={showCalendar}
            onClose={() => setShowCalendar(false)}
            contentHistory={contentHistory}
            onContentCreated={() => {
              setShowCalendar(false);
              // Could open the mindmap to create new content
            }}
            onSendToStudio={({ title, script }) => {
              localStorage.setItem('studio_prefill', JSON.stringify({ title, script }));
              setShowCalendar(false);
              onNavigateToStudio?.();
            }}
          />
        )}
      </AnimatePresence>

      {/* Chat Panel */}
      <AnimatePresence>
        {showChatPanel && (
          <ChatPanel
            onClose={() => setShowChatPanel(false)}
            context={getChatContext()}
            onTopicSelect={(topic) => {
              setSelectedTopic({
                topic,
                audience: selectedAudience || 'patients',
                contentType: selectedContentType || 'blog',
                output: selectedContentType ? CONTENT_TYPES[selectedContentType].output : 'Draft',
                viralHook: '',
                keywords: keywords,
              });
              setShowModal(true);
            }}
            onKeywordSave={handleSaveKeyword}
          />
        )}
      </AnimatePresence>

      {/* Mindmap */}
      <div className="mindmap-container dot-grid">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.3 }}
          minZoom={0.5}
          maxZoom={1.5}
          defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
        >
          <Background color="#E4E4E7" gap={20} size={1} />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>

      {/* Instructions Panel */}
      <motion.div 
        className="instructions-panel"
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 100, delay: 0.2 }}
      >
        <p className="instructions-text" data-testid="instructions-text">
          {!selectedAudience && (
            <>Click an <strong>audience</strong> to explore content ideas</>
          )}
          {selectedAudience && !selectedContentType && (
            <>
              {selectedAudience === 'patients' ? (
                <>Select a <strong>therapeutic area</strong> then choose a <strong>content type</strong></>
              ) : (
                <>Select a <strong>content type</strong> to generate topics</>
              )}
            </>
          )}
          {selectedAudience && selectedContentType && isLoadingTopics && (
            <span className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Generating viral topics...
            </span>
          )}
          {selectedAudience && selectedContentType && !isLoadingTopics && topics.length > 0 && (
            <>Click a <strong>topic</strong> to generate content • Use <strong>chat</strong> to brainstorm</>
          )}
        </p>
      </motion.div>

      {/* Generation Modal */}
      <AnimatePresence>
        {showModal && (
          <GenerationModal
            isOpen={showModal}
            onClose={() => {
              setShowModal(false);
              setGeneratedContent(null);
            }}
            topic={selectedTopic}
            generatedContent={generatedContent}
            isGenerating={isGenerating}
            onGenerate={handleGenerateContent}
          />
        )}
      </AnimatePresence>

      {/* Help Guide */}
      <HelpGuide 
        isOpen={showHelpGuide} 
        onClose={() => setShowHelpGuide(false)} 
      />
    </div>
  );
};

export default ContentMindmap;
